import React, { useMemo, useState, useEffect } from "react";
import { Form, Button, Modal, InputGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
import ObjectFieldSelector from "../fieldselector/ObjectFieldSelector";
import PicklistFieldSelector from "./PicklistFieldSelector";
import ObjectConfigApi from "../../api/ObjectConfigApi";
import DateFilterLiteralsHelpModal from "../common/DateFilterLiteralsHelpModal";
import DateLiteralsHintLink from "../common/DateLiteralsHintLink";
import LoggedUserMergeTextInput from "../common/LoggedUserMergeTextInput";
import LookupLayout from "../layout/LookupLayout";
import { isDateOrDateTimeFieldType } from "../../utils/dateLiteralUtils";
import {
  LOGGED_USER_COMPARE_FIELDS,
  encodeLoggedUserCompareValue,
} from "../../utils/filterFieldValueUtils";
import * as constants from "../../constants/CONSTANT";

function normalizeFieldName(fieldName) {
  if (typeof fieldName !== "string") return fieldName || "";
  return fieldName.replace(/{{\s*|\s*}}/g, "").trim();
}

function toPlaceholder(fieldName) {
  const f = normalizeFieldName(fieldName);
  if (!f) return "";
  return `{{${f}}}`;
}

function toParentPlaceholder(childLookupFieldName, parentFieldName) {
  const lf = normalizeFieldName(childLookupFieldName);
  const pf = normalizeFieldName(parentFieldName);
  if (!lf || !pf) return "";
  return `{{${lf}.${pf}}}`;
}

function toLoggedUserPlaceholder(fieldName) {
  const f = normalizeFieldName(fieldName);
  if (!f) return "";
  return encodeLoggedUserCompareValue(f);
}

function isLookupTargetField(field) {
  if (!field) return false;
  const dt = String(field.data_type || "").toLowerCase();
  return dt === "lookup" || field.is_reference === true;
}

function isPlainStaticNewValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value !== "string") return true;
  return !value.includes("{{");
}

/**
 * Address source field names for geocode UI + persistence.
 * Preserves empty strings so "Add field" placeholder rows are not stripped on re-render
 * (filter(Boolean) made extra rows disappear immediately).
 */
function deriveGeoAddressSourceFields(row) {
  if (Array.isArray(row?.geoAddressSourceFields)) {
    return row.geoAddressSourceFields.map((x) => normalizeFieldName(x ?? ""));
  }
  const legacy = normalizeFieldName(row?.geoAddressSourceField || "");
  return legacy ? [legacy] : [];
}

export default function WorkflowFieldUpdate({
  config,
  onChange,
  objectName,
  readOnly,
}) {
  const [showValueFieldSelectorForRow, setShowValueFieldSelectorForRow] = useState(null); // number | null
  const [showDateHelpModal, setShowDateHelpModal] = useState(false);
  const [valueSourceMode, setValueSourceMode] = useState("self"); // "self" | "parent"
  const [valueSourceParentRelationId, setValueSourceParentRelationId] = useState("");
  const [availableFields, setAvailableFields] = useState([]);
  const [parentFieldsCache, setParentFieldsCache] = useState({}); // objectName -> fields[]
  const [parentRelations, setParentRelations] = useState([]); // related_list_config rows for (parent -> this object)

  const fieldUpdates = Array.isArray(config?.fieldUpdates) ? config.fieldUpdates : null;

  const emptyGeo = () => ({
    geoAddressSourceFields: [],
    geoCoordinateTargetMode: "split",
    geoLatLngCombinedField: "",
    geoLatitudeField: "",
    geoLongitudeField: "",
    geoCoordinateSourceMode: "split",
    geoLatLngSourceField: "",
    geoLatitudeSourceField: "",
    geoLongitudeSourceField: "",
    geoAddressTargetMode: "formatted",
    geoAddressTargetField: "",
    geoRevTargetFormattedField: "",
    geoRevTargetStreetField: "",
    geoRevTargetCityField: "",
    geoRevTargetStateField: "",
    geoRevTargetCountryField: "",
    geoRevTargetPostalField: "",
  });

  const derivedRows = useMemo(() => {
    if (fieldUpdates && fieldUpdates.length > 0) {
      return fieldUpdates.map((r) => ({
        target: (r?.target || "self"),
        fieldUpdateKind: String(r?.fieldUpdateKind || "standard").toLowerCase(),
        fieldName: r?.fieldName || "",
        newValue: r?.newValue ?? "",
        staticLookupLabel: r?.staticLookupLabel || "",
        parentObjectName: r?.parentObjectName || "",
        parentLookupFieldName: r?.parentLookupFieldName || "",
        parentRelationId: r?.parentRelationId || "",
        geoAddressSourceFields: deriveGeoAddressSourceFields(r),
        geoCoordinateTargetMode:
          String(r?.geoCoordinateTargetMode || "split").toLowerCase() === "combined"
            ? "combined"
            : "split",
        geoLatLngCombinedField: r?.geoLatLngCombinedField || "",
        geoLatitudeField: r?.geoLatitudeField || "",
        geoLongitudeField: r?.geoLongitudeField || "",
        geoCoordinateSourceMode:
          String(r?.geoCoordinateSourceMode || "split").toLowerCase() === "combined"
            ? "combined"
            : "split",
        geoLatLngSourceField: r?.geoLatLngSourceField || "",
        geoLatitudeSourceField: r?.geoLatitudeSourceField || "",
        geoLongitudeSourceField: r?.geoLongitudeSourceField || "",
        geoAddressTargetMode:
          String(r?.geoAddressTargetMode || "formatted").toLowerCase() === "components"
            ? "components"
            : "formatted",
        geoAddressTargetField: r?.geoAddressTargetField || "",
        geoRevTargetFormattedField: r?.geoRevTargetFormattedField || "",
        geoRevTargetStreetField: r?.geoRevTargetStreetField || "",
        geoRevTargetCityField: r?.geoRevTargetCityField || "",
        geoRevTargetStateField: r?.geoRevTargetStateField || "",
        geoRevTargetCountryField: r?.geoRevTargetCountryField || "",
        geoRevTargetPostalField: r?.geoRevTargetPostalField || "",
      }));
    }
    // Legacy config: single fieldName/newValue
    if (config?.fieldName) {
      return [
        {
          target: "self",
          fieldUpdateKind: "standard",
          fieldName: config.fieldName,
          newValue: config?.newValue ?? "",
          staticLookupLabel: config?.staticLookupLabel || "",
          parentObjectName: "",
          parentLookupFieldName: "",
          parentRelationId: "",
          ...emptyGeo(),
        },
      ];
    }
    return [
      {
        target: "self",
        fieldUpdateKind: "standard",
        fieldName: "",
        newValue: "",
        staticLookupLabel: "",
        parentObjectName: "",
        parentLookupFieldName: "",
        parentRelationId: "",
        ...emptyGeo(),
      },
    ];
  }, [fieldUpdates, config?.fieldName, config?.newValue]);

  const setRows = (nextRows) => {
    const safe = Array.isArray(nextRows) ? nextRows : [];
    onChange("fieldUpdates", safe);

    // Keep legacy keys in sync for maximum backwards compatibility
    const k0 = String(safe[0]?.fieldUpdateKind || "standard").toLowerCase();
    if (
      safe.length === 1 &&
      String(safe[0]?.target || "self").toLowerCase() !== "parent" &&
      k0 === "standard"
    ) {
      onChange("fieldName", safe[0]?.fieldName || "");
      onChange("newValue", safe[0]?.newValue ?? "");
    }
  };

  // Fetch all fields when objectName changes
  useEffect(() => {
    const fetchFields = async () => {
      if (!objectName) {
        setAvailableFields([]);
        return;
      }

      try {
        const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
        if (response.success && response.data?.fields) {
          // Filter out readonly, calculated, and rollupsummary fields
          const filteredFields = response.data.fields.filter(f => {
            const dataType = (f.data_type || '').toLowerCase();
            return !f.is_readonly &&
              dataType !== 'calculated' &&
              dataType !== 'rollupsummary';
          });
          setAvailableFields(filteredFields);
        }
      } catch (error) {
        console.error("Error fetching fields:", error);
        setAvailableFields([]);
      }
    };

    fetchFields();
  }, [objectName]);

  // Fetch object list (for parent object selection)
  useEffect(() => {
    // Fetch related_list_config rows where current object is the TARGET (child).
    // These define valid parent relationships: source_object_name (parent) -> target_object_name (child).
    const fetchParentRelations = async () => {
      if (!objectName) {
        setParentRelations([]);
        return;
      }
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${constants.API_BASE_URL}/api/relatedlist/target/${encodeURIComponent(
            String(objectName).trim()
          )}`,
          {
            method: "GET",
            mode: "cors",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
          }
        );
        const json = await response.json();
        if (json?.success && Array.isArray(json.data)) {
          setParentRelations(json.data);
        } else {
          setParentRelations([]);
        }
      } catch {
        setParentRelations([]);
      }
    };
    fetchParentRelations();
  }, [objectName]);

  const getFilteredUpdatableFields = (fields) => {
    const list = Array.isArray(fields) ? fields : [];
    return list.filter((f) => {
      const dataType = String(f?.data_type || "").toLowerCase();
      return !f?.is_readonly && dataType !== "calculated" && dataType !== "rollupsummary";
    });
  };

  const parentRelationOptions = useMemo(() => {
    // Build unique (source_object_name + target_field_name) options; keep stable display.
    const list = Array.isArray(parentRelations) ? parentRelations : [];
    return list
      .filter((r) => r && r.source_object_name && r.target_field_name)
      .map((r) => {
        const id = String(r.id || "").trim();
        const parentObject = String(r.source_object_name).trim();
        const childLookupField = String(r.target_field_name).trim();
        const label =
          String(r.relationship_label || r.relationship_name || "").trim() ||
          `${parentObject} via ${childLookupField}`;
        // relatedlist/target API includes aggregated parent object fields as `object_fields`
        // (for the source_object_name). Use it to avoid extra fetches.
        let parentObjectFields = r.object_fields;
        if (typeof parentObjectFields === "string") {
          try {
            parentObjectFields = JSON.parse(parentObjectFields);
          } catch {
            parentObjectFields = null;
          }
        }
        return {
          id,
          parentObject,
          childLookupField,
          label,
          parentObjectFields: Array.isArray(parentObjectFields) ? parentObjectFields : null,
        };
      });
  }, [parentRelations]);

  const ensureParentFieldsLoaded = async (parentObjectName) => {
    const key = String(parentObjectName || "").trim();
    if (!key) return;
    if (Array.isArray(parentFieldsCache[key]) && parentFieldsCache[key].length > 0) {
      return;
    }
    try {
      const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(key);
      if (response.success && Array.isArray(response.data?.fields)) {
        const filtered = getFilteredUpdatableFields(response.data.fields);
        setParentFieldsCache((prev) => ({ ...prev, [key]: filtered }));
      }
    } catch (e) {
      // ignore
    }
  };

  const hydrateParentFieldsCacheIfPresent = (parentObjectName, rawFields) => {
    const key = String(parentObjectName || "").trim();
    if (!key) return false;
    if (Array.isArray(parentFieldsCache[key]) && parentFieldsCache[key].length > 0) return true;
    if (!Array.isArray(rawFields) || rawFields.length === 0) return false;
    const filtered = getFilteredUpdatableFields(rawFields);
    setParentFieldsCache((prev) => ({ ...prev, [key]: filtered }));
    return true;
  };

  const parentOptionById = useMemo(() => {
    const m = new Map();
    for (const opt of parentRelationOptions || []) {
      if (opt?.id) m.set(String(opt.id), opt);
    }
    return m;
  }, [parentRelationOptions]);

  // When editing an existing workflow, parent rows may already have parentRelationId/parentObjectName saved.
  // Ensure parent field dropdowns are hydrated on initial load (without requiring user re-selection).
  useEffect(() => {
    const run = async () => {
      const rows = Array.isArray(derivedRows) ? derivedRows : [];
      for (const row of rows) {
        const target = String(row?.target || "self").toLowerCase();
        if (target !== "parent") continue;

        const relId = row?.parentRelationId ? String(row.parentRelationId) : "";
        const opt = relId ? parentOptionById.get(relId) : null;
        const po = String(row?.parentObjectName || opt?.parentObject || "").trim();
        if (!po) continue;

        // First hydrate from related_list_config (if present), then fallback to fetch.
        const hydrated = hydrateParentFieldsCacheIfPresent(po, opt?.parentObjectFields);
        if (!hydrated) {
          await ensureParentFieldsLoaded(po);
        }
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentOptionById, objectName]);

  const handleValueFieldSelectForRow = (rowIndex, field) => {
    const next = [...derivedRows];
    next[rowIndex] = { ...next[rowIndex], newValue: toPlaceholder(field?.field_name) };
    setRows(next);
    setShowValueFieldSelectorForRow(null);
  };

  const handleParentValueFieldSelectForRow = (rowIndex, parentOpt, field) => {
    const token = toParentPlaceholder(parentOpt?.childLookupField, field?.field_name);
    const next = [...derivedRows];
    next[rowIndex] = { ...next[rowIndex], newValue: token };
    setRows(next);
    setShowValueFieldSelectorForRow(null);
  };

  const handleLoggedUserValueFieldSelectForRow = (rowIndex, fieldName) => {
    const token = toLoggedUserPlaceholder(fieldName);
    const next = [...derivedRows];
    next[rowIndex] = { ...next[rowIndex], newValue: token };
    setRows(next);
    setShowValueFieldSelectorForRow(null);
  };

  const getSelectedValueFieldNameForRow = (rowIndex) => {
    const v = derivedRows?.[rowIndex]?.newValue;
    if (v && typeof v === "string" && v.includes("{{")) {
      const match = v.match(/\{\{(.+?)\}\}/);
      return match ? match[1].trim() : null;
    }
    return null;
  };

  const getTargetFieldsForRow = (row) => {
    const target = String(row?.target || "self").toLowerCase();
    if (target !== "parent") return availableFields || [];
    const po = String(row?.parentObjectName || "").trim();
    return parentFieldsCache[po] || [];
  };

  const getSelectedTargetField = (row) => {
    const fields = getTargetFieldsForRow(row);
    const fn = row?.fieldName;
    if (!fn) return null;
    return fields.find((f) => f.field_name === fn) || null;
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="fw-semibold">Field Updates</div>
        {!readOnly && (
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() =>
              setRows([
                ...derivedRows,
                {
                  target: "self",
                  fieldUpdateKind: "standard",
                  fieldName: "",
                  newValue: "",
                  staticLookupLabel: "",
                  parentObjectName: "",
                  parentLookupFieldName: "",
                  parentRelationId: "",
                  ...emptyGeo(),
                },
              ])
            }
          >
            <i className="fa fa-plus me-1"></i> Add Field
          </Button>
        )}
      </div>

      {derivedRows.map((row, idx) => {
        const target = String(row?.target || "self").toLowerCase();
        const updateKind = String(row.fieldUpdateKind || "standard").toLowerCase();
        const selectedTargetField =
          updateKind === "standard" ? getSelectedTargetField(row) : null;
        const isPicklistField =
          selectedTargetField &&
          (selectedTargetField.data_type === "picklist" || selectedTargetField.data_type === "multipicklist");
        const isMultiPicklist = selectedTargetField?.data_type === "multipicklist";
        const isLookupField = isLookupTargetField(selectedTargetField);
        const lookupRefObject = isLookupField
          ? selectedTargetField.reference_object ||
            selectedTargetField.referenceObject ||
            ""
          : "";
        const lookupDisplayFields = isLookupField
          ? selectedTargetField.display_fields ||
            selectedTargetField.displayFields ||
            "name"
          : "";
        const lookupSearchFields = isLookupField
          ? selectedTargetField.lookup_search_fields ||
            selectedTargetField.lookupSearchFields ||
            lookupDisplayFields
          : "";
        const isTokenValue =
          typeof row?.newValue === "string" &&
          row.newValue.includes("{{") &&
          row.newValue.includes("}}");
        const showDateHint =
          updateKind === "standard" &&
          selectedTargetField &&
          isDateOrDateTimeFieldType(selectedTargetField.data_type);
        const isTimeField =
          updateKind === "standard" &&
          selectedTargetField &&
          String(selectedTargetField.data_type || "").toLowerCase() === "time";
        const dateValuePlaceholder =
          "e.g. TODAY, 2024-06-01, or {{fieldname}}";
        const triggerFields = availableFields || [];
        const writeFields = getTargetFieldsForRow(row);
        const targetFieldsDisabled =
          readOnly || (target === "parent" && !row.parentObjectName);

        return (
          <div key={idx} className="border rounded p-3 mb-3" style={{ background: "#fff" }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fw-semibold">Update #{idx + 1}</div>
              {!readOnly && derivedRows.length > 1 && (
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => setRows(derivedRows.filter((_, i) => i !== idx))}
                >
                  <i className="fa fa-trash me-1"></i> Remove
                </Button>
              )}
            </div>

            <Form.Group className="mb-2">
              <Form.Label className="mb-1">Target</Form.Label>
              <Form.Select
                value={row.target || "self"}
                disabled={readOnly}
                onChange={async (e) => {
                  const next = [...derivedRows];
                  const v = e.target.value;
                  next[idx] = {
                    ...next[idx],
                    target: v,
                    fieldName: "",
                    newValue: "",
                    staticLookupLabel: "",
                    ...emptyGeo(),
                  };
                  setRows(next);
                  if (String(v).toLowerCase() === "parent") {
                    // best-effort preload if parent object already set
                    const po = next[idx]?.parentObjectName;
                    if (po) await ensureParentFieldsLoaded(po);
                  }
                }}
              >
                <option value="self">This record</option>
                <option value="parent">Parent record</option>
              </Form.Select>
            </Form.Group>

            {target === "parent" && (
              <>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1">Parent Relationship</Form.Label>
                  <Form.Select
                    value={row.parentRelationId || ""}
                    disabled={readOnly}
                    onChange={async (e) => {
                      const relId = e.target.value;
                      const rel = parentRelationOptions.find((x) => String(x.id) === String(relId)) || null;
                      const po = rel?.parentObject || "";
                      const lookupField = rel?.childLookupField || "";
                      const next = [...derivedRows];
                      next[idx] = {
                        ...next[idx],
                        parentRelationId: relId,
                        parentObjectName: po,
                        parentLookupFieldName: lookupField,
                        fieldName: "",
                        newValue: "",
                        staticLookupLabel: "",
                        geoLatitudeField: "",
                        geoLongitudeField: "",
                        geoLatLngCombinedField: "",
                        geoLatLngSourceField: "",
                        geoLatitudeSourceField: "",
                        geoLongitudeSourceField: "",
                        geoAddressTargetField: "",
                        geoRevTargetFormattedField: "",
                        geoRevTargetStreetField: "",
                        geoRevTargetCityField: "",
                        geoRevTargetStateField: "",
                        geoRevTargetCountryField: "",
                        geoRevTargetPostalField: "",
                      };
                      setRows(next);
                      if (po) {
                        // Prefer fields already returned by related_list_config (low cost)
                        const hydrated = hydrateParentFieldsCacheIfPresent(
                          po,
                          rel?.parentObjectFields
                        );
                        if (!hydrated) {
                          await ensureParentFieldsLoaded(po);
                        }
                      }
                    }}
                  >
                    <option value="">-Select Relationship-</option>
                    {parentRelationOptions.map((opt) => (
                      <option key={opt.id || `${opt.parentObject}:${opt.childLookupField}`} value={opt.id}>
                        {opt.label} ({opt.parentObject} ← {opt.childLookupField})
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Only relationships from `related_list_config` are shown (source = parent, target = this object).
                  </Form.Text>
                </Form.Group>

                <div className="small text-muted mb-2">
                  Parent object: <span className="fw-semibold">{row.parentObjectName || "-"}</span>
                  {"  "}·{"  "}
                  Link field on this object: <span className="fw-semibold">{row.parentLookupFieldName || "-"}</span>
                </div>
              </>
            )}

            <Form.Group className="mb-2">
              <Form.Label className="mb-1">Update type</Form.Label>
              <Form.Select
                value={updateKind}
                disabled={readOnly}
                onChange={(e) => {
                  const next = [...derivedRows];
                  const kind = e.target.value;
                  next[idx] = {
                    ...next[idx],
                    fieldUpdateKind: kind,
                    fieldName: "",
                    newValue: "",
                    staticLookupLabel: "",
                    ...emptyGeo(),
                    ...(kind === "geocode" ? { geoAddressSourceFields: [""] } : {}),
                  };
                  setRows(next);
                }}
              >
                <option value="standard">Standard — set field value</option>
                <option value="geocode">Geocode — address → latitude &amp; longitude</option>
                <option value="reverse_geocode">Reverse geocode — latitude &amp; longitude → address</option>
              </Form.Select>

            </Form.Group>

            {updateKind === "standard" && (
              <>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1">Field Name</Form.Label>
                  <Form.Select
                    value={row.fieldName || ""}
                    required={!readOnly}
                    disabled={readOnly || (target === "parent" && !row.parentObjectName)}
                    onChange={(e) => {
                      const next = [...derivedRows];
                      next[idx] = {
                        ...next[idx],
                        fieldName: e.target.value,
                        newValue: "",
                        staticLookupLabel: "",
                      };
                      setRows(next);
                    }}
                  >
                    <option value="">-Select Field-</option>
                    {writeFields.map((field) => (
                      <option key={field.field_name} value={field.field_name}>
                        {field.field_label || field.field_name}
                      </option>
                    ))}
                  </Form.Select>
                  {target === "parent" &&
                    row.parentObjectName &&
                    (!parentFieldsCache[String(row.parentObjectName).trim()] ||
                      parentFieldsCache[String(row.parentObjectName).trim()].length === 0) && (
                      <Form.Text className="text-muted">
                        Loading parent fields…
                      </Form.Text>
                    )}
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label className="mb-1">
                    New Value
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip>
                          {isPicklistField
                            ? "Select picklist value(s) from the dropdown"
                            : isLookupField
                              ? "Search and select a related record, or map from another field"
                              : showDateHint
                                ? "Enter TODAY, YYYY-MM-DD, or {{fieldname}} (see date literals help)."
                                : "Enter a value or select a field (uses {{fieldname}})."}
                        </Tooltip>
                      }
                    >
                      <i className="fa fa-info-circle mx-2 text-muted"></i>
                    </OverlayTrigger>
                  </Form.Label>

                  {isPicklistField ? (
                    <>
                      {isTokenValue ? (
                        <>
                          <div className="d-flex gap-2 align-items-start">
                            <div className="flex-grow-1">
                              <LoggedUserMergeTextInput
                                value={row.newValue ?? ""}
                                disabled={readOnly}
                                onChange={(val) => {
                                  const next = [...derivedRows];
                                  next[idx] = { ...next[idx], newValue: val };
                                  setRows(next);
                                }}
                              />
                            </div>
                            {!readOnly && (
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-secondary"
                                  onClick={() => setShowValueFieldSelectorForRow(idx)}
                                >
                                  <i className="fa fa-list me-1"></i> Browse Fields
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  onClick={() => {
                                    const next = [...derivedRows];
                                    next[idx] = {
                                      ...next[idx],
                                      newValue: "",
                                      staticLookupLabel: "",
                                    };
                                    setRows(next);
                                  }}
                                >
                                  Clear
                                </Button>
                              </div>
                            )}
                          </div>
                          <Form.Text className="text-muted">
                            Using a field token for this picklist value.
                          </Form.Text>
                        </>
                      ) : (
                        <>
                          <div className="d-flex gap-2 align-items-start">
                            <div className="flex-grow-1">
                              <PicklistFieldSelector
                                field={selectedTargetField}
                                value={row.newValue || ""}
                                onChange={(value) => {
                                  const next = [...derivedRows];
                                  next[idx] = { ...next[idx], newValue: value };
                                  setRows(next);
                                }}
                                disabled={readOnly}
                                isMultiSelect={isMultiPicklist}
                              />
                            </div>
                            {!readOnly && (
                              <Button
                                variant="outline-secondary"
                                onClick={() => setShowValueFieldSelectorForRow(idx)}
                              >
                                <i className="fa fa-list me-1"></i> Browse Fields
                              </Button>
                            )}
                          </div>
                          {!readOnly && (
                            <Form.Text className="text-muted d-block">
                              Or copy value from another field (inserts a token like{" "}
                              <span className="font-monospace">{`{{field}}`}</span>).
                            </Form.Text>
                          )}
                        </>
                      )}
                    </>
                  ) : isLookupField ? (
                    <>
                      {isTokenValue ? (
                        <>
                          <div className="d-flex gap-2 align-items-start">
                            <div className="flex-grow-1">
                              <LoggedUserMergeTextInput
                                value={row.newValue ?? ""}
                                disabled={readOnly}
                                onChange={(val) => {
                                  const next = [...derivedRows];
                                  next[idx] = {
                                    ...next[idx],
                                    newValue: val,
                                    staticLookupLabel: "",
                                  };
                                  setRows(next);
                                }}
                              />
                            </div>
                            {!readOnly && (
                              <div className="d-flex gap-2">
                                <Button
                                  variant="outline-secondary"
                                  onClick={() => setShowValueFieldSelectorForRow(idx)}
                                >
                                  <i className="fa fa-list me-1"></i> Browse Fields
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  onClick={() => {
                                    const next = [...derivedRows];
                                    next[idx] = {
                                      ...next[idx],
                                      newValue: "",
                                      staticLookupLabel: "",
                                    };
                                    setRows(next);
                                  }}
                                >
                                  Clear
                                </Button>
                              </div>
                            )}
                          </div>
                          <Form.Text className="text-muted">
                            Using a field token or logged user value for this lookup.
                          </Form.Text>
                        </>
                      ) : !lookupRefObject ? (
                        <Form.Text className="text-muted">
                          This lookup has no reference object configured. Use Browse Fields
                          to map from another field, or type a record id.
                        </Form.Text>
                      ) : (
                        <>
                          <div className="d-flex gap-2 align-items-start">
                            <div className="flex-grow-1">
                              <LookupLayout
                                objectName={lookupRefObject}
                                searchFields={lookupSearchFields}
                                displayFields={lookupDisplayFields}
                                value={
                                  isPlainStaticNewValue(row.newValue) && row.newValue
                                    ? String(row.newValue)
                                    : ""
                                }
                                defaultLabel={row.staticLookupLabel || ""}
                                onChange={(id, label) => {
                                  const next = [...derivedRows];
                                  next[idx] = {
                                    ...next[idx],
                                    newValue: id || "",
                                    staticLookupLabel: label || "",
                                  };
                                  setRows(next);
                                }}
                                disabled={readOnly}
                                placeholder="Search record..."
                              />
                            </div>
                            {!readOnly && (
                              <Button
                                variant="outline-secondary"
                                onClick={() => setShowValueFieldSelectorForRow(idx)}
                              >
                                <i className="fa fa-list me-1"></i> Browse Fields
                              </Button>
                            )}
                          </div>
                          {!readOnly && (
                            <Form.Text className="text-muted d-block">
                              Or copy value from another field (inserts a token like{" "}
                              <span className="font-monospace">{`{{field}}`}</span>
                              ), or use logged user fields via{" "}
                              <span className="font-monospace">{`{{LoggedUser.`}</span>.
                            </Form.Text>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <InputGroup>
                        <LoggedUserMergeTextInput
                          type={
                            isTimeField && !isTokenValue ? "time" : "text"
                          }
                          placeholder={
                            showDateHint
                              ? dateValuePlaceholder
                              : isTimeField
                                ? "HH:MM or {{field}}"
                                : "Type {{LoggedUser. for user fields"
                          }
                          value={
                            isTimeField && !isTokenValue
                              ? (() => {
                                  let v = String(row.newValue ?? "");
                                  if (!v) return "";
                                  if (v.includes("T") || v.includes(" ")) {
                                    const d = new Date(v);
                                    if (!Number.isNaN(d.getTime())) {
                                      return (
                                        String(d.getHours()).padStart(2, "0") +
                                        ":" +
                                        String(d.getMinutes()).padStart(2, "0")
                                      );
                                    }
                                  }
                                  if (v.includes(":")) {
                                    const parts = v.split(":");
                                    return parts[0] + ":" + parts[1];
                                  }
                                  return v;
                                })()
                              : row.newValue ?? ""
                          }
                          onChange={(val) => {
                            const next = [...derivedRows];
                            next[idx] = { ...next[idx], newValue: val };
                            setRows(next);
                          }}
                          disabled={readOnly}
                        />
                        {!readOnly && (
                          <Button
                            variant="outline-secondary"
                            onClick={() => setShowValueFieldSelectorForRow(idx)}
                          >
                            <i className="fa fa-list me-1"></i> Browse Fields
                          </Button>
                        )}
                      </InputGroup>
                      {showDateHint && (
                        <DateLiteralsHintLink onOpen={() => setShowDateHelpModal(true)} />
                      )}
                      <Form.Text className="text-muted">
                        {showDateHint
                          ? "Use date literals (TODAY, LAST_N_DAYS:7) or a fixed YYYY-MM-DD date, or copy from another field."
                          : isTimeField
                            ? "Pick a time, or use Browse Fields to copy from another field."
                            : "Type a value, pick a record field, or type {{LoggedUser. to pick a user field."}
                      </Form.Text>
                    </>
                  )}
                </Form.Group>
              </>
            )}

            {updateKind === "geocode" && (
              <>
                <Form.Group className="mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="mb-0">Address source fields (on workflow record)</Form.Label>
                    {!readOnly && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => {
                          const next = [...derivedRows];
                          const list = [...(next[idx].geoAddressSourceFields || []), ""];
                          next[idx] = { ...next[idx], geoAddressSourceFields: list };
                          setRows(next);
                        }}
                      >
                        <i className="fa fa-plus me-1"></i> Add field
                      </Button>
                    )}
                  </div>
                  <Form.Text className="text-muted d-block mb-2">
                    Values are combined in list order, separated by commas (e.g. street, city, state).
                  </Form.Text>
                  {(row.geoAddressSourceFields || []).length === 0 && !readOnly && (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="mb-2"
                      onClick={() => {
                        const next = [...derivedRows];
                        next[idx] = { ...next[idx], geoAddressSourceFields: [""] };
                        setRows(next);
                      }}
                    >
                      Add address field
                    </Button>
                  )}
                  {(row.geoAddressSourceFields || []).map((fn, partIdx) => (
                    <InputGroup key={partIdx} className="mb-2">
                      <Form.Select
                        value={fn || ""}
                        disabled={readOnly}
                        onChange={(e) => {
                          const next = [...derivedRows];
                          const list = [...(next[idx].geoAddressSourceFields || [])];
                          list[partIdx] = e.target.value;
                          next[idx] = { ...next[idx], geoAddressSourceFields: list };
                          setRows(next);
                        }}
                      >
                        <option value="">-Select Field-</option>
                        {triggerFields.map((field) => (
                          <option key={field.field_name} value={field.field_name}>
                            {field.field_label || field.field_name}
                          </option>
                        ))}
                      </Form.Select>
                      {!readOnly && (
                        <Button
                          variant="outline-danger"
                          title="Remove this field from the address"
                          onClick={() => {
                            const next = [...derivedRows];
                            const list = (next[idx].geoAddressSourceFields || []).filter(
                              (_, i) => i !== partIdx
                            );
                            next[idx] = { ...next[idx], geoAddressSourceFields: list };
                            setRows(next);
                          }}
                        >
                          <i className="fa fa-times"></i>
                        </Button>
                      )}
                    </InputGroup>
                  ))}
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1">Coordinate storage</Form.Label>
                  <Form.Select
                    value={row.geoCoordinateTargetMode === "combined" ? "combined" : "split"}
                    disabled={targetFieldsDisabled}
                    onChange={(e) => {
                      const next = [...derivedRows];
                      const mode = e.target.value === "combined" ? "combined" : "split";
                      next[idx] = {
                        ...next[idx],
                        geoCoordinateTargetMode: mode,
                        geoLatitudeField: "",
                        geoLongitudeField: "",
                        geoLatLngCombinedField: "",
                      };
                      setRows(next);
                    }}
                  >
                    <option value="split">Two fields — latitude and longitude separately</option>
                    <option value="combined">One field — latitude and longitude as &quot;lat, lng&quot;</option>
                  </Form.Select>
                </Form.Group>
                {row.geoCoordinateTargetMode === "combined" ? (
                  <Form.Group className="mb-2">
                    <Form.Label className="mb-1">Latitude &amp; longitude — write to single field</Form.Label>
                    <Form.Select
                      value={row.geoLatLngCombinedField || ""}
                      disabled={targetFieldsDisabled}
                      onChange={(e) => {
                        const next = [...derivedRows];
                        next[idx] = { ...next[idx], geoLatLngCombinedField: e.target.value };
                        setRows(next);
                      }}
                    >
                      <option value="">-Select Field-</option>
                      {writeFields.map((field) => (
                        <option key={field.field_name} value={field.field_name}>
                          {field.field_label || field.field_name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Stored as comma-separated values (e.g. 28.6129, 77.2295).
                    </Form.Text>
                  </Form.Group>
                ) : (
                  <>
                    <Form.Group className="mb-2">
                      <Form.Label className="mb-1">Latitude — write to field</Form.Label>
                      <Form.Select
                        value={row.geoLatitudeField || ""}
                        disabled={targetFieldsDisabled}
                        onChange={(e) => {
                          const next = [...derivedRows];
                          next[idx] = { ...next[idx], geoLatitudeField: e.target.value };
                          setRows(next);
                        }}
                      >
                        <option value="">-Select Field-</option>
                        {writeFields.map((field) => (
                          <option key={field.field_name} value={field.field_name}>
                            {field.field_label || field.field_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label className="mb-1">Longitude — write to field</Form.Label>
                      <Form.Select
                        value={row.geoLongitudeField || ""}
                        disabled={targetFieldsDisabled}
                        onChange={(e) => {
                          const next = [...derivedRows];
                          next[idx] = { ...next[idx], geoLongitudeField: e.target.value };
                          setRows(next);
                        }}
                      >
                        <option value="">-Select Field-</option>
                        {writeFields.map((field) => (
                          <option key={field.field_name} value={field.field_name}>
                            {field.field_label || field.field_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </>
                )}
              </>
            )}

            {updateKind === "reverse_geocode" && (
              <>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1">Coordinate source (on workflow record)</Form.Label>
                  <Form.Select
                    value={row.geoCoordinateSourceMode === "combined" ? "combined" : "split"}
                    disabled={readOnly}
                    onChange={(e) => {
                      const next = [...derivedRows];
                      const mode = e.target.value === "combined" ? "combined" : "split";
                      next[idx] = {
                        ...next[idx],
                        geoCoordinateSourceMode: mode,
                        geoLatitudeSourceField: "",
                        geoLongitudeSourceField: "",
                        geoLatLngSourceField: "",
                      };
                      setRows(next);
                    }}
                  >
                    <option value="split">Two fields — latitude and longitude separately</option>
                    <option value="combined">One field — value like &quot;lat, lng&quot;</option>
                  </Form.Select>
                </Form.Group>
                {row.geoCoordinateSourceMode === "combined" ? (
                  <Form.Group className="mb-2">
                    <Form.Label className="mb-1">Latitude &amp; longitude source field</Form.Label>
                    <Form.Select
                      value={row.geoLatLngSourceField || ""}
                      disabled={readOnly}
                      onChange={(e) => {
                        const next = [...derivedRows];
                        next[idx] = { ...next[idx], geoLatLngSourceField: e.target.value };
                        setRows(next);
                      }}
                    >
                      <option value="">-Select Field-</option>
                      {triggerFields.map((field) => (
                        <option key={field.field_name} value={field.field_name}>
                          {field.field_label || field.field_name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Same format as geocode &quot;one field&quot; output (e.g. 19.076, 72.877).
                    </Form.Text>
                  </Form.Group>
                ) : (
                  <>
                    <Form.Group className="mb-2">
                      <Form.Label className="mb-1">Latitude source field</Form.Label>
                      <Form.Select
                        value={row.geoLatitudeSourceField || ""}
                        disabled={readOnly}
                        onChange={(e) => {
                          const next = [...derivedRows];
                          next[idx] = { ...next[idx], geoLatitudeSourceField: e.target.value };
                          setRows(next);
                        }}
                      >
                        <option value="">-Select Field-</option>
                        {triggerFields.map((field) => (
                          <option key={field.field_name} value={field.field_name}>
                            {field.field_label || field.field_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label className="mb-1">Longitude source field</Form.Label>
                      <Form.Select
                        value={row.geoLongitudeSourceField || ""}
                        disabled={readOnly}
                        onChange={(e) => {
                          const next = [...derivedRows];
                          next[idx] = { ...next[idx], geoLongitudeSourceField: e.target.value };
                          setRows(next);
                        }}
                      >
                        <option value="">-Select Field-</option>
                        {triggerFields.map((field) => (
                          <option key={field.field_name} value={field.field_name}>
                            {field.field_label || field.field_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </>
                )}
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1">Address result storage</Form.Label>
                  <Form.Select
                    value={row.geoAddressTargetMode === "components" ? "components" : "formatted"}
                    disabled={targetFieldsDisabled}
                    onChange={(e) => {
                      const next = [...derivedRows];
                      const m = e.target.value === "components" ? "components" : "formatted";
                      next[idx] = {
                        ...next[idx],
                        geoAddressTargetMode: m,
                        geoAddressTargetField: "",
                        geoRevTargetFormattedField: "",
                        geoRevTargetStreetField: "",
                        geoRevTargetCityField: "",
                        geoRevTargetStateField: "",
                        geoRevTargetCountryField: "",
                        geoRevTargetPostalField: "",
                      };
                      setRows(next);
                    }}
                  >
                    <option value="formatted">One field — full formatted address</option>
                    <option value="components">Multiple fields — formatted line, street, city, …</option>
                  </Form.Select>
                </Form.Group>
                {row.geoAddressTargetMode === "components" ? (
                  <>
                    <Form.Label className="small text-muted mb-2">
                      Component targets
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`wf-reverse-geocode-components-${idx}`}>
                            Map each part you need; leave unused rows blank. At least one target
                            field is required.
                          </Tooltip>
                        }
                      >
                        <i
                          className="fa fa-info-circle text-muted ms-2"
                          role="img"
                          aria-label="Help for component targets"
                          style={{ cursor: "help" }}
                        />
                      </OverlayTrigger>
                    </Form.Label>
                    {[
                      ["geoRevTargetFormattedField", "Full formatted address"],
                      ["geoRevTargetStreetField", "Street line (number + street)"],
                      ["geoRevTargetCityField", "City"],
                      ["geoRevTargetStateField", "State / region"],
                      ["geoRevTargetCountryField", "Country"],
                      ["geoRevTargetPostalField", "Postal code"],
                    ].map(([key, label]) => (
                      <Form.Group className="mb-2" key={key}>
                        <Form.Label className="mb-1 small">{label}</Form.Label>
                        <Form.Select
                          value={row[key] || ""}
                          disabled={targetFieldsDisabled}
                          onChange={(e) => {
                            const next = [...derivedRows];
                            next[idx] = { ...next[idx], [key]: e.target.value };
                            setRows(next);
                          }}
                        >
                          <option value="">— None —</option>
                          {writeFields.map((field) => (
                            <option key={field.field_name} value={field.field_name}>
                              {field.field_label || field.field_name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    ))}
                  </>
                ) : (
                  <Form.Group className="mb-2">
                    <Form.Label className="mb-1">Address — write to field</Form.Label>
                    <Form.Select
                      value={row.geoAddressTargetField || ""}
                      disabled={targetFieldsDisabled}
                      onChange={(e) => {
                        const next = [...derivedRows];
                        next[idx] = { ...next[idx], geoAddressTargetField: e.target.value };
                        setRows(next);
                      }}
                    >
                      <option value="">-Select Field-</option>
                      {writeFields.map((field) => (
                        <option key={field.field_name} value={field.field_name}>
                          {field.field_label || field.field_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}
              </>
            )}
          </div>
        );
      })}

      <Modal
        show={showValueFieldSelectorForRow != null}
        onHide={() => setShowValueFieldSelectorForRow(null)}
        size="lg"
        centered
        onEntered={async () => {
          // Default to selecting from this record; keep previous parent selection for convenience.
          setValueSourceMode("self");
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Field for Value</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label className="mb-1">Value Source</Form.Label>
            <Form.Select
              value={valueSourceMode}
              onChange={(e) => setValueSourceMode(e.target.value)}
            >
              <option value="self">This record field</option>
              <option value="parent">Parent record field</option>
              <option value="logged_user">Logged user field</option>
            </Form.Select>
          </Form.Group>

          {valueSourceMode === "logged_user" && (
            <Form.Group className="mb-2">
              <Form.Label className="mb-1">Current user field</Form.Label>
              <Form.Select
                defaultValue=""
                onChange={(e) => {
                  const fn = e.target.value;
                  if (!fn || showValueFieldSelectorForRow == null) return;
                  handleLoggedUserValueFieldSelectForRow(
                    showValueFieldSelectorForRow,
                    fn
                  );
                  e.target.value = "";
                }}
              >
                <option value="">-Select field-</option>
                {LOGGED_USER_COMPARE_FIELDS.map((f) => (
                  <option key={f.field_name} value={f.field_name}>
                    {f.field_label} ({`{{LoggedUser.${f.field_name}}}`})
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Inserts a token resolved to the acting user when the workflow runs
                (or the API user for validation).
              </Form.Text>
            </Form.Group>
          )}

          {valueSourceMode === "parent" && (
            <Form.Group className="mb-2">
              <Form.Label className="mb-1">Parent Relationship</Form.Label>
              <Form.Select
                value={valueSourceParentRelationId || ""}
                onChange={async (e) => {
                  const relId = e.target.value;
                  setValueSourceParentRelationId(relId);
                  const opt = parentOptionById.get(String(relId));
                  if (opt?.parentObject) {
                    await ensureParentFieldsLoaded(opt.parentObject);
                  }
                }}
              >
                <option value="">-Select Relationship-</option>
                {parentRelationOptions.map((opt) => (
                  <option key={opt.id || `${opt.parentObject}:${opt.childLookupField}`} value={opt.id}>
                    {opt.label} ({opt.parentObject} ← {opt.childLookupField})
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Inserts tokens like <span className="font-monospace">{`{{lookupField.parentField}}`}</span>
              </Form.Text>
            </Form.Group>
          )}

          {valueSourceMode !== "parent" && valueSourceMode !== "logged_user" && (
            <ObjectFieldSelector
              objectName={objectName}
              multiSelect={false}
              onSelect={(field) =>
                handleValueFieldSelectForRow(showValueFieldSelectorForRow, field)
              }
              excludeReadOnly={true}
              defaultSelectedFieldName={getSelectedValueFieldNameForRow(
                showValueFieldSelectorForRow
              )}
              selectionUi="button"
              enableSearch={true}
            />
          )}

          {valueSourceMode === "parent" && (() => {
            const opt = valueSourceParentRelationId
              ? parentOptionById.get(String(valueSourceParentRelationId))
              : null;
            const parentObject = opt?.parentObject || "";
            if (!parentObject) {
              return (
                <div className="text-muted small">
                  Select a parent relationship to browse parent fields.
                </div>
              );
            }
            return (
              <ObjectFieldSelector
                objectName={parentObject}
                multiSelect={false}
                onSelect={(field) =>
                  handleParentValueFieldSelectForRow(showValueFieldSelectorForRow, opt, field)
                }
                excludeReadOnly={true}
                selectionUi="button"
                enableSearch={true}
              />
            );
          })()}
        </Modal.Body>
      </Modal>

      <DateFilterLiteralsHelpModal
        show={showDateHelpModal}
        onHide={() => setShowDateHelpModal(false)}
        purpose="fieldUpdate"
      />
    </>
  );
}
